import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getMe } from "@/lib/socialpulseOidc";
import { VerifiedBadge, PageToolbar, usePageTranslation } from "@/components/PageToolbar";
import { useMemo } from "react";

export default function ThreadDetail() {
  const { id } = useParams();
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState({ author: "", body: "" });
  const [me, setMe] = useState(null);

  const load = () => api.get(`/forum/threads/${id}`).then((r) => setThread(r.data));
  useEffect(() => {
    window.scrollTo(0, 0);
    load();
    getMe().then(setMe);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!reply.body.trim()) return toast.error("Write a reply first");
    await api.post(`/forum/threads/${id}/posts`, reply);
    toast.success("Reply posted");
    setReply({ author: "", body: "" });
    load();
  };

  if (!thread) return <main className="max-w-3xl mx-auto px-6 py-24 text-center font-display text-2xl text-[#6b5d51]">Loading…</main>;

  return <ThreadView thread={thread} reply={reply} setReply={setReply} submit={submit} me={me} />;
}

function ThreadView({ thread, reply, setReply, submit, me }) {
  const blocks = useMemo(() => {
    const b = { title: thread.title, body: thread.body };
    thread.posts.forEach((p, i) => (b[`p${i}`] = p.body));
    return b;
  }, [thread]);
  const { lang, tr, busy } = usePageTranslation(blocks, `thread-${thread.id}`);
  const speakText = [tr("title", thread.title), tr("body", thread.body), ...thread.posts.map((p, i) => tr(`p${i}`, p.body))].join(". ");

  return (
    <main data-testid="thread-detail-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <Link to="/community" data-testid="back-to-community-link" className="text-sm font-medium link-underline text-[#7d1a1a]">← All Discussions</Link>
      <span className="block font-mono-label text-[10px] tracking-[0.18em] uppercase bg-[#efe7d7] px-2 py-1 w-fit mt-6">{thread.category}</span>
      <h1 data-testid="thread-title" className="font-display font-bold text-3xl sm:text-4xl mt-3 text-[#1c1512]">{tr("title", thread.title)}</h1>
      <p className="font-mono-label text-[10px] text-[#8a7c6d] mt-2">by {thread.author} · {new Date(thread.created).toLocaleDateString()}</p>
      <div className="mt-4">
        <PageToolbar speakText={speakText} busy={busy} lang={lang} />
      </div>
      <p className="mt-5 text-lg leading-relaxed text-[#2b241d] bg-[#fbf8f1] border border-[#d8ccb8] p-5">{tr("body", thread.body)}</p>

      <h2 className="font-display font-bold text-xl mt-10 rule-thick pt-4">{thread.posts.length} Replies</h2>
      <div className="mt-4 space-y-3">
        {thread.posts.map((p, i) => (
          <div key={i} data-testid={`thread-post-${i}`} className="bg-[#efe7d7] p-4 border-l-4 border-[#7d1a1a]">
            <p className="text-[#2b241d]">{tr(`p${i}`, p.body)}</p>
            <p className="font-mono-label text-[10px] text-[#8a7c6d] mt-2 flex items-center gap-1.5">
              — {p.author} {p.verified && <VerifiedBadge />} · {new Date(p.created).toLocaleDateString()}
            </p>
          </div>
        ))}
        {thread.posts.length === 0 && <p className="text-[#6b5d51]">No replies yet — be the first.</p>}
      </div>

      <form data-testid="reply-form" onSubmit={submit} className="mt-8 bg-[#fbf8f1] border-2 border-[#1c1512] p-5 space-y-3">
        <textarea
          data-testid="reply-body-input"
          value={reply.body}
          onChange={(e) => setReply({ ...reply, body: e.target.value })}
          placeholder="Add your reply…"
          rows={3}
          className="w-full bg-transparent border border-[#d8ccb8] focus:border-[#7d1a1a] outline-none p-3 text-sm"
        />
        <div className="flex flex-wrap gap-3 items-center">
          {me ? (
            <span data-testid="posting-as-label" className="text-sm text-[#6b5d51]">
              Posting as <span className="font-medium text-[#1c1512]">{me.name}</span> <VerifiedBadge />
            </span>
          ) : (
            <input
              data-testid="reply-author-input"
              value={reply.author}
              onChange={(e) => setReply({ ...reply, author: e.target.value })}
              placeholder="Your name (optional)"
              className="bg-transparent border border-[#d8ccb8] focus:border-[#7d1a1a] outline-none px-3 py-2 text-sm"
            />
          )}
          <button data-testid="reply-submit-button" type="submit" className="bg-[#7d1a1a] text-[#f6f1e7] px-5 py-2 font-medium hover:bg-[#5c1220] transition-colors">
            Post Reply
          </button>
        </div>
      </form>
    </main>
  );
}
